// V1 source: customer/midas/components/MidasArtifactTableRenderer.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 table renderer — Tier B forward-compat. Bot doesn't actively emit table
// artifacts today (Round 14: only image / diagram_overlay / action_form are
// active). This V2 viewer keeps dock chrome consistent if the bot later
// emits tables. Verified against the dev-only debug harness
// (`?midas-debug-artifact=table`) since there is no real-traffic case.
//
// Logic copied verbatim from V1:
//   - STATUS_COLORS map for status / priority cell highlighting
//   - getStatusColor (lowercase + strip whitespace key)
//   - isStatusColumn(key) — "status" | "priority" | "state"
//   - compareCells(a, b, dir) numeric vs string compare
//   - formatCellValue — ISO date detection + locale formatting
//   - Sort state: sortKey + sortDir, toggle on header click
//   - handleRowClick — opens row.link in new tab if it starts with "/"
//
// Visual layer rewritten in Tailwind. Lucide-react icons replace MUI icons.
// Native shadcn Tooltip for cell ellipsis tooltips.

import React, { useMemo, useState } from "react";
import { ArrowUp, ArrowDown, ExternalLink } from "lucide-react";

import { ArtifactTab } from "../../midasPageModels";
import {
  TableArtifactData,
  TableArtifactColumn,
} from "../../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

// ===================================================================
//  Status color map (verbatim from V1)
// ===================================================================

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  open: { bg: "rgba(52,152,219,0.08)", text: "#3498DB", border: "#3498DB" },
  new: { bg: "rgba(52,152,219,0.08)", text: "#3498DB", border: "#3498DB" },
  inprogress: { bg: "rgba(245,158,11,0.08)", text: "#F59E0B", border: "#F59E0B" },
  in_progress: { bg: "rgba(245,158,11,0.08)", text: "#F59E0B", border: "#F59E0B" },
  pending: { bg: "rgba(245,158,11,0.08)", text: "#F59E0B", border: "#F59E0B" },
  completed: { bg: "rgba(39,174,96,0.08)", text: "#27AE60", border: "#27AE60" },
  done: { bg: "rgba(39,174,96,0.08)", text: "#27AE60", border: "#27AE60" },
  closed: { bg: "rgba(107,114,128,0.08)", text: "#6B7280", border: "#6B7280" },
  cancelled: { bg: "rgba(192,57,43,0.08)", text: "#C0392B", border: "#C0392B" },
  rejected: { bg: "rgba(192,57,43,0.08)", text: "#C0392B", border: "#C0392B" },
  high: { bg: "rgba(192,57,43,0.08)", text: "#C0392B", border: "#C0392B" },
  medium: { bg: "rgba(245,158,11,0.08)", text: "#F59E0B", border: "#F59E0B" },
  low: { bg: "rgba(39,174,96,0.08)", text: "#27AE60", border: "#27AE60" },
};

const FALLBACK_STATUS = {
  bg: "rgba(140,131,117,0.08)",
  text: "#8C8375",
  border: "#D8D2C4",
};

const getStatusColor = (value: string) => {
  const key = value.toLowerCase().replace(/\s+/g, "");
  return STATUS_COLORS[key] || FALLBACK_STATUS;
};

const isStatusColumn = (key: string): boolean => {
  const lower = key.toLowerCase();
  return lower === "status" || lower === "priority" || lower === "state";
};

// ===================================================================
//  Sorting (verbatim from V1)
// ===================================================================

type SortDir = "asc" | "desc";

const compareCells = (a: any, b: any, dir: SortDir): number => {
  const aVal = a ?? "";
  const bVal = b ?? "";
  if (typeof aVal === "number" && typeof bVal === "number") {
    return dir === "asc" ? aVal - bVal : bVal - aVal;
  }
  const cmp = String(aVal).localeCompare(String(bVal), undefined, {
    sensitivity: "base",
  });
  return dir === "asc" ? cmp : -cmp;
};

// ===================================================================
//  Format helpers (verbatim from V1)
// ===================================================================

const formatCellValue = (value: any): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
    try {
      const d = new Date(value);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return value;
    }
  }
  return String(value);
};

// ===================================================================
//  MidasArtifactTableRendererV2
// ===================================================================

interface Props {
  tab: ArtifactTab;
}

const MidasArtifactTableRendererV2: React.FC<Props> = ({ tab }) => {
  const { translate } = useTranslation("chatBot");
  const data = tab.artifact.data as TableArtifactData | undefined;

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const columns: TableArtifactColumn[] = data?.columns ?? [];
  const rows = data?.rows ?? [];

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => compareCells(a[sortKey], b[sortKey], sortDir));
  }, [rows, sortKey, sortDir]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleRowClick = (row: Record<string, any>) => {
    const link = row.link;
    if (typeof link === "string" && link.startsWith("/")) {
      window.open(link, "_blank", "noopener,noreferrer");
    }
  };

  if (!data || columns.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center font-sora text-[13px] text-midas-muted-stone">
        {translate("midas.artifact.noData")}
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Title + count */}
      <div className="flex-shrink-0 border-b border-midas-stone px-4 py-[10px]">
        <div className="font-sora text-[13px] font-semibold text-midas-graphite">
          {tab.artifact.title}
        </div>
        {data.total_count != null && (
          <div className="mt-[2px] font-mono text-[11px] text-midas-muted-stone">
            {data.total_count} {translate("midas.artifact.table.totalItems")}
          </div>
        )}
      </div>

      {/* Scrollable table area */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse font-sora text-[12px]">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  style={{ width: col.width || "auto" }}
                  className="sticky top-0 cursor-pointer select-none whitespace-nowrap border-b border-midas-stone bg-midas-bone-light px-3 py-2 text-left font-sora text-[10px] font-bold uppercase tracking-[0.5px] text-midas-warm-graphite transition-colors duration-150 hover:bg-midas-warm-stone/60"
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key &&
                      (sortDir === "asc" ? (
                        <ArrowUp size={12} className="text-mid-blue" />
                      ) : (
                        <ArrowDown size={12} className="text-mid-blue" />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedRows.map((row, rowIdx) => {
              const hasLink =
                typeof row.link === "string" && row.link.startsWith("/");
              return (
                <tr
                  key={rowIdx}
                  onClick={() => handleRowClick(row)}
                  className={[
                    "transition-colors duration-150",
                    hasLink
                      ? "cursor-pointer hover:bg-midas-warm-stone/40"
                      : "cursor-default",
                  ].join(" ")}
                >
                  {columns.map((col) => {
                    const value = row[col.key];
                    const isStatus = isStatusColumn(col.key);
                    const formatted = formatCellValue(value);

                    return (
                      <td
                        key={col.key}
                        className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap border-b border-midas-warm-stone px-3 py-2 text-midas-graphite"
                      >
                        {isStatus && value ? (
                          <StatusPill
                            label={String(value)}
                            colors={getStatusColor(String(value))}
                          />
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-sora text-[12px]">
                                {formatted}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{formatted}</TooltipContent>
                          </Tooltip>
                        )}
                      </td>
                    );
                  })}

                  {hasLink && (
                    <td className="w-[24px] border-b border-midas-warm-stone px-[6px] py-2">
                      <ExternalLink
                        size={14}
                        className="text-midas-muted-stone"
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===================================================================
//  StatusPill — Tailwind rewrite of RedesignStatusBadge.
//  V1 used a shared MUI-based RedesignStatusBadge component; v2/ stays
//  MUI-free, so we inline the pill chrome here using the same status
//  color palette. Keeps the v2/ MUI-free gate green.
// ===================================================================

const StatusPill: React.FC<{
  label: string;
  colors: { bg: string; text: string; border: string };
}> = ({ label, colors }) => (
  <span
    className="inline-flex items-center rounded-pill border px-[8px] py-[1px] font-sora text-[11px] font-semibold"
    style={{
      backgroundColor: colors.bg,
      borderColor: colors.border,
      color: colors.text,
    }}
  >
    {label}
  </span>
);

export default MidasArtifactTableRendererV2;
